/* Sign-in, registration and the forced password change (ADM-01).
 *
 * A split panel: the product on the left, the form on the right. The left
 * panel exists to tell someone who has just been sent a link what this
 * application is before they are asked for a credential - a bare form on a
 * white page tells them nothing.
 *
 * Self-registration says plainly that the account will wait for an
 * administrator. A form that looked like it granted access and then silently
 * produced a pending account would send the person to a sign-in screen that
 * rejects them for no visible reason.
 */

import { useState } from 'react'
import { auth, type AuthMode, type Me } from '../api/auth'
import { VersigentLogo, VersigentMark } from '../components/Brand'

type Panel = 'signin' | 'register' | 'change'

interface Props {
  mode: AuthMode
  onSignedIn: (me: Me) => void
}

export function SignIn({ mode, onSignedIn }: Props) {
  const [panel, setPanel] = useState<Panel>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [requestedRole, setRequestedRole] = useState('planner')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      if (panel === 'signin') {
        const me = await auth.login(email, password)
        // A bootstrap or reset password is valid for exactly this one
        // sign-in, so the change is demanded before anything else loads.
        if (me.must_change_password) {
          setPanel('change')
          setNotice('This password was issued for a single sign-in. Choose your own to continue.')
        } else {
          onSignedIn(me)
        }
      } else if (panel === 'register') {
        const result = await auth.register(email, fullName, password, [requestedRole])
        setNotice(result.detail)
        setPanel('signin')
        setPassword('')
      } else {
        if (newPassword !== confirmPassword) throw new Error('the two passwords do not match')
        await auth.changePassword(password, newPassword)
        setNotice('Password changed. Sign in with the new one.')
        setPanel('signin')
        setPassword('')
        setNewPassword('')
        setConfirmPassword('')
      }
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const heading =
    panel === 'signin' ? 'Sign in' : panel === 'register' ? 'Request access' : 'Choose a password'

  return (
    <div className="signin">
      {/* ---------------------------------------------------- left panel */}
      <aside className="signin-brand">
        <div className="signin-watermark" aria-hidden="true">
          <VersigentMark size={620} tone="light" />
        </div>

        <div className="signin-brand-top">
          <VersigentLogo size={30} tone="light" subtitle={null} />
        </div>

        <div className="signin-brand-body">
          <h1>
            Capacity
            <br />
            Study
          </h1>
          <span className="signin-rule" aria-hidden="true" />
          <p>
            Master Production Scheduling for wiring-harness plants.
            <br />
            Deterministic calculations, auditable decisions.
          </p>
        </div>

        <footer className="signin-brand-foot">
          Versigent — Production Control &amp; Logistics
        </footer>
      </aside>

      {/* --------------------------------------------------- right panel */}
      <main className="signin-form-pane">
        <div className="signin-form">
          <div className="signin-form-logo">
            <VersigentLogo size={34} subtitle="Capacity Study" />
          </div>

          <h2>{heading}</h2>

          {panel === 'signin' && mode.self_registration && (
            <p className="signin-sub">
              No account yet?{' '}
              <a
                href="#register"
                onClick={(e) => {
                  e.preventDefault()
                  setPanel('register')
                  setError(null)
                }}
              >
                Request access
              </a>
            </p>
          )}
          {panel === 'register' && (
            <p className="signin-sub">
              An administrator approves the account and assigns its plants.{' '}
              <a
                href="#signin"
                onClick={(e) => {
                  e.preventDefault()
                  setPanel('signin')
                  setError(null)
                }}
              >
                Back to sign in
              </a>
            </p>
          )}
          {panel === 'change' && (
            <p className="signin-sub">The password you were given is valid for this sign-in only.</p>
          )}

          {notice && <div className="banner good">{notice}</div>}
          {error && (
            <div className="banner critical">
              <span aria-hidden="true">⚠</span> {error}
            </div>
          )}

          <form onSubmit={submit}>
            {panel === 'register' && (
              <Field id="name" label="Full name">
                <input
                  id="name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  autoComplete="name"
                />
              </Field>
            )}

            {panel !== 'change' && (
              <Field id="email" label="Email address" required>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="username"
                  placeholder="name@versigent.com"
                />
              </Field>
            )}

            <Field id="pw" label={panel === 'change' ? 'Current password' : 'Password'} required>
              <input
                id="pw"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete={panel === 'register' ? 'new-password' : 'current-password'}
              />
            </Field>

            {panel === 'register' && (
              <Field id="role" label="Role requested">
                <select
                  id="role"
                  value={requestedRole}
                  onChange={(e) => setRequestedRole(e.target.value)}
                >
                  {mode.roles
                    .filter((r) => r.id !== 'administrator')
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.id.replace('_', ' ')}
                      </option>
                    ))}
                </select>
                <span className="field-hint">
                  {mode.roles.find((r) => r.id === requestedRole)?.description}
                </span>
              </Field>
            )}

            {panel === 'change' && (
              <>
                <Field
                  id="np"
                  label={`New password (at least ${mode.min_password_length} characters)`}
                  required
                >
                  <input
                    id="np"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={mode.min_password_length}
                    autoComplete="new-password"
                  />
                </Field>
                <Field id="cp" label="Confirm new password" required>
                  <input
                    id="cp"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </Field>
              </>
            )}

            <button className="primary signin-submit" type="submit" disabled={busy}>
              {busy
                ? 'Working…'
                : panel === 'signin'
                  ? 'Sign in'
                  : panel === 'register'
                    ? 'Request access'
                    : 'Change password'}
            </button>
          </form>

          <p className="signin-note">
            {panel === 'register'
              ? 'Access requests are approved by a system administrator.'
              : 'Every action is recorded against your account.'}
          </p>
        </div>
      </main>
    </div>
  )
}

function Field({
  id,
  label,
  required,
  children,
}: {
  id: string
  label: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </label>
      {children}
    </div>
  )
}
